import * as React from "react"
import * as SwitchPrimitives from "@radix-ui/react-switch"

import { cn } from "@/lib/utils"

/**
 * Touch-friendly switch component with:
 * - Larger touch target (meets 44px height requirement per WCAG 2.5.5)
 * - Visual feedback for touch interactions
 * - Smooth transitions for state changes
 */

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      // Base styles - increased from h-6 w-11 to h-7 w-12 for better touch
      "peer inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full",
      // Border and background
      "border-2 border-transparent",
      // Transitions
      "transition-colors",
      // Focus styles
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      // Disabled states
      "disabled:cursor-not-allowed disabled:opacity-50",
      // State colors
      "data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      // Touch-friendly enhancements
      "touch-action-manipulation select-none",
      // Active state feedback
      "active:scale-[0.97] transition-transform duration-100",
      className
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        // Base styles - increased from h-5 w-5 to h-6 w-6 for proportional size
        "pointer-events-none block h-6 w-6 rounded-full bg-background shadow-lg ring-0",
        // Transition for smooth sliding - increased translate for new width
        "transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
      )}
    />
  </SwitchPrimitives.Root>
))
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
